import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  Tooltip,
  Skeleton,
} from '@mui/material';
import {
  Fullscreen as FullscreenIcon,
  Close as CloseIcon,
  LocationOn as LocationIcon,
  Directions as DirectionsIcon,
} from '@mui/icons-material';
import { GoogleMap, useJsApiLoader, InfoWindow } from '@react-google-maps/api';

// Type declaration for AdvancedMarkerElement until types are updated
declare global {
  interface Window {
    google: any;
  }
}

interface LeadLocationMapProps {
  location: {
    id: string;
    name?: string;
    streetLine1?: string;
    streetLine2?: string;
    city?: string;
    province?: string;
    postalCode?: string;
    country?: string;
    latitude?: number | null;
    longitude?: number | null;
  };
  leadName: string;
  compact?: boolean;
}

// Define libraries outside component to prevent re-renders
const libraries: ("places" | "drawing" | "geometry" | "localContext" | "visualization")[] = ['places'];

// Get the API key and Map ID from environment
const GOOGLE_MAPS_API_KEY = (import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string) || '';
const GOOGLE_MAPS_MAP_ID = (import.meta.env.VITE_GOOGLE_MAPS_MAP_ID as string) || 'DEMO_MAP_ID';

export const LeadLocationMap: React.FC<LeadLocationMapProps> = ({ location, leadName, compact = false }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [mapCenter, setMapCenter] = useState({ lat: 0, lng: 0 });
  const [showInfoWindow, setShowInfoWindow] = useState(false);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);
  const advancedMarkerRef = useRef<any>(null);

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-maps-script-sites',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: libraries,
  });

  const containerStyle = {
    width: '100%',
    height: isFullscreen ? '80vh' : '200px',
    borderRadius: '8px',
  };

  const fullAddress = [
    location.streetLine1,
    location.streetLine2,
    location.city,
    location.province,
    location.postalCode,
    location.country
  ].filter(Boolean).join(', ');

  // Set initial map center based on coordinates or use default
  useEffect(() => {
    if (location.latitude && location.longitude) {
      setMapCenter({ lat: Number(location.latitude), lng: Number(location.longitude) });
    } else {
      // Default to Ottawa if no coordinates
      setMapCenter({ lat: 45.4215, lng: -75.6972 });
    }
  }, [location]);

  const handleOpenInGoogleMaps = () => {
    const query = encodeURIComponent(fullAddress);
    window.open(`https://www.google.com/maps/search/?api=1&query=${query}`, '_blank');
  };

  const handleGetDirections = () => {
    const destination = encodeURIComponent(fullAddress);
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${destination}`, '_blank');
  };

  const createAdvancedMarker = useCallback((map: any, position: { lat: number; lng: number }) => {
    if (!isLoaded || !window.google?.maps?.marker?.AdvancedMarkerElement) {
      return null;
    }

    // Clean up existing marker
    if (advancedMarkerRef.current) {
      advancedMarkerRef.current.map = null;
    }

    // Create new advanced marker
    const marker = new window.google.maps.marker.AdvancedMarkerElement({
      map: map,
      position: position,
      title: leadName,
    });

    // Add click listener for info window
    marker.addListener('click', () => {
      setShowInfoWindow(true);
    });

    advancedMarkerRef.current = marker;
    return marker;
  }, [isLoaded, leadName]);

  // Update marker position when mapCenter changes
  useEffect(() => {
    if (mapInstance && isLoaded) {
      createAdvancedMarker(mapInstance, mapCenter);
    }
  }, [mapCenter, mapInstance, isLoaded, createAdvancedMarker]);

  // Cleanup marker on unmount
  useEffect(() => {
    return () => {
      if (advancedMarkerRef.current) {
        advancedMarkerRef.current.map = null;
      }
    };
  }, []);

  // Handle load error
  if (loadError) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <LocationIcon sx={{ fontSize: 32, color: 'error.main', mb: 1 }} />
        <Typography variant="body2" color="error">
          Failed to load Google Maps
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Please check your internet connection and try again
        </Typography>
      </Box>
    );
  }

  if (compact) {
    // Compact mode for inline use
    return (
      <>
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 600 }}>
              Map View {isGeocoding && '(Loading...)'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Get directions">
                <IconButton size="small" onClick={handleGetDirections}>
                  <DirectionsIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Open in Google Maps">
                <IconButton size="small" onClick={handleOpenInGoogleMaps}>
                  <LocationIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="View fullscreen">
                <IconButton size="small" onClick={() => setIsFullscreen(true)}>
                  <FullscreenIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Compact map */}
          <Box sx={{ position: 'relative', borderRadius: 1, overflow: 'hidden' }}>
            {!isLoaded ? (
              <Skeleton variant="rectangular" height={150} />
            ) : GOOGLE_MAPS_API_KEY ? (
              <GoogleMap
                mapContainerStyle={{ width: '100%', height: '150px', borderRadius: '8px' }}
                center={mapCenter}
                zoom={15}
                options={{
                  zoomControl: false,
                  streetViewControl: false,
                  mapTypeControl: false,
                  fullscreenControl: false,
                  mapId: GOOGLE_MAPS_MAP_ID, // Required for AdvancedMarkerElement
                }}
                onLoad={(map) => {
                  setMapInstance(map);
                  
                  // Create marker
                  createAdvancedMarker(map, mapCenter);
                  
                  // Geocode if we don't have coordinates
                  if (!location.latitude && !location.longitude && fullAddress) {
                    setIsGeocoding(true);
                    const geocoder = new window.google.maps.Geocoder();
                    geocoder.geocode({ address: fullAddress }, (results, status) => {
                      setIsGeocoding(false);
                      if (status === 'OK' && results && results[0]) {
                        const { lat, lng } = results[0].geometry.location;
                        const newCenter = { lat: lat(), lng: lng() };
                        setMapCenter(newCenter);
                        map.setCenter(newCenter);
                        createAdvancedMarker(map, newCenter);
                      } else {
                        console.warn('Geocoding failed for address:', fullAddress, 'Status:', status);
                        // Keep the default center (Ottawa) and still show the map
                      }
                    });
                  }
                }}
              >
                {showInfoWindow && (
                  <InfoWindow
                    position={mapCenter}
                    onCloseClick={() => setShowInfoWindow(false)}
                  >
                    <Box sx={{ p: 1 }}>
                      <Typography variant="subtitle2">{leadName}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {location.streetLine1}
                      </Typography>
                    </Box>
                  </InfoWindow>
                )}
              </GoogleMap>
            ) : (
              <Box 
                sx={{ 
                  height: 150, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  bgcolor: 'grey.100',
                  border: '2px dashed',
                  borderColor: 'grey.300'
                }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <LocationIcon sx={{ fontSize: 32, color: 'grey.400', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                    Google Maps API key not configured
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Set VITE_GOOGLE_MAPS_API_KEY to enable maps
                  </Typography>
                </Box>
              </Box>
            )}
            
            {/* Click overlay for compact map - only if maps are available */}
            {GOOGLE_MAPS_API_KEY && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: 'rgba(0,0,0,0.05)',
                  },
                }}
                onClick={() => setIsFullscreen(true)}
              />
            )}
          </Box>
        </Box>

        {/* Fullscreen Dialog */}
        <Dialog
          open={isFullscreen}
          onClose={() => setIsFullscreen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h6">{leadName}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {fullAddress}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title="Get directions">
                  <IconButton onClick={handleGetDirections}>
                    <DirectionsIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Open in Google Maps">
                  <IconButton onClick={handleOpenInGoogleMaps}>
                    <LocationIcon />
                  </IconButton>
                </Tooltip>
                <IconButton onClick={() => setIsFullscreen(false)}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            {GOOGLE_MAPS_API_KEY && isLoaded ? (
              <GoogleMap
                mapContainerStyle={{ width: '100%', height: '80vh' }}
                center={mapCenter}
                zoom={16}
                options={{
                  zoomControl: true,
                  streetViewControl: true,
                  mapTypeControl: true,
                  fullscreenControl: false,
                  mapId: GOOGLE_MAPS_MAP_ID, // Required for AdvancedMarkerElement
                }}
                onLoad={(map) => {
                  setMapInstance(map);
                  
                  // Create marker
                  createAdvancedMarker(map, mapCenter);
                  
                  // Geocode if we don't have coordinates
                  if (!location.latitude && !location.longitude && fullAddress) {
                    setIsGeocoding(true);
                    const geocoder = new window.google.maps.Geocoder();
                    geocoder.geocode({ address: fullAddress }, (results, status) => {
                      setIsGeocoding(false);
                      if (status === 'OK' && results && results[0]) {
                        const { lat, lng } = results[0].geometry.location;
                        const newCenter = { lat: lat(), lng: lng() };
                        setMapCenter(newCenter);
                        map.setCenter(newCenter);
                        createAdvancedMarker(map, newCenter);
                      } else {
                        console.warn('Geocoding failed for address:', fullAddress, 'Status:', status);
                        // Keep the default center (Ottawa) and still show the map
                      }
                    });
                  }
                }}
              >
              </GoogleMap>
            ) : (
              <Box 
                sx={{ 
                  height: '80vh', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center'
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  Google Maps API key not configured
                </Typography>
              </Box>
            )}
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // Full card mode (original)
  return (
    <>
      <Card sx={{ position: 'relative' }}>
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" color="primary">
              Location Map
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Get directions">
                <IconButton size="small" onClick={handleGetDirections}>
                  <DirectionsIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Open in Google Maps">
                <IconButton size="small" onClick={handleOpenInGoogleMaps}>
                  <LocationIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="View fullscreen">
                <IconButton size="small" onClick={() => setIsFullscreen(true)}>
                  <FullscreenIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Address display */}
          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 0.5 }}>
              {location.name || 'Address'}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {location.streetLine1}
              {location.streetLine2 && `, ${location.streetLine2}`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {[location.city, location.province, location.postalCode].filter(Boolean).join(', ')}
              {location.country && location.country !== 'Canada' && `, ${location.country}`}
            </Typography>
          </Box>

          {/* Mini map */}
          <Box sx={{ position: 'relative', borderRadius: 1, overflow: 'hidden' }}>
            {!isLoaded ? (
              <Skeleton variant="rectangular" height={200} />
            ) : GOOGLE_MAPS_API_KEY ? (
              <GoogleMap
                mapContainerStyle={containerStyle}
                center={mapCenter}
                zoom={15}
                options={{
                  zoomControl: false,
                  streetViewControl: false,
                  mapTypeControl: false,
                  fullscreenControl: false,
                  mapId: GOOGLE_MAPS_MAP_ID, // Required for AdvancedMarkerElement
                }}
                onLoad={(map) => {
                  setMapInstance(map);
                  
                  // Create marker
                  createAdvancedMarker(map, mapCenter);
                  
                  // Geocode if we don't have coordinates
                  if (!location.latitude && !location.longitude && fullAddress) {
                    setIsGeocoding(true);
                    const geocoder = new window.google.maps.Geocoder();
                    geocoder.geocode({ address: fullAddress }, (results, status) => {
                      setIsGeocoding(false);
                      if (status === 'OK' && results && results[0]) {
                        const { lat, lng } = results[0].geometry.location;
                        const newCenter = { lat: lat(), lng: lng() };
                        setMapCenter(newCenter);
                        map.setCenter(newCenter);
                        createAdvancedMarker(map, newCenter);
                      } else {
                        console.warn('Geocoding failed for address:', fullAddress, 'Status:', status);
                        // Keep the default center (Ottawa) and still show the map
                      }
                    });
                  }
                }}
              >
                {showInfoWindow && (
                  <InfoWindow
                    position={mapCenter}
                    onCloseClick={() => setShowInfoWindow(false)}
                  >
                    <Box sx={{ p: 1 }}>
                      <Typography variant="subtitle2">{leadName}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {location.streetLine1}
                      </Typography>
                    </Box>
                  </InfoWindow>
                )}
              </GoogleMap>
            ) : (
              <Box 
                sx={{ 
                  height: 200, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  bgcolor: 'grey.100',
                  border: '2px dashed',
                  borderColor: 'grey.300'
                }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <LocationIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Google Maps API key not configured
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Set VITE_GOOGLE_MAPS_API_KEY to enable maps
                  </Typography>
                </Box>
              </Box>
            )}
            
            {/* Click overlay for mini map - only if maps are available */}
            {GOOGLE_MAPS_API_KEY && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: 'rgba(0,0,0,0.05)',
                  },
                }}
                onClick={() => setIsFullscreen(true)}
              />
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Fullscreen Dialog */}
      <Dialog
        open={isFullscreen}
        onClose={() => setIsFullscreen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h6">{leadName}</Typography>
              <Typography variant="body2" color="text.secondary">
                {fullAddress}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Get directions">
                <IconButton onClick={handleGetDirections}>
                  <DirectionsIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Open in Google Maps">
                <IconButton onClick={handleOpenInGoogleMaps}>
                  <LocationIcon />
                </IconButton>
              </Tooltip>
              <IconButton onClick={() => setIsFullscreen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {GOOGLE_MAPS_API_KEY && isLoaded ? (
            <GoogleMap
              mapContainerStyle={{ width: '100%', height: '80vh' }}
              center={mapCenter}
              zoom={16}
              options={{
                zoomControl: true,
                streetViewControl: true,
                mapTypeControl: true,
                fullscreenControl: false,
                mapId: GOOGLE_MAPS_MAP_ID, // Required for AdvancedMarkerElement
              }}
              onLoad={(map) => {
                setMapInstance(map);
                
                // Create marker
                createAdvancedMarker(map, mapCenter);
                
                // Geocode if we don't have coordinates
                if (!location.latitude && !location.longitude && fullAddress) {
                  const geocoder = new window.google.maps.Geocoder();
                  geocoder.geocode({ address: fullAddress }, (results, status) => {
                    if (status === 'OK' && results && results[0]) {
                      const { lat, lng } = results[0].geometry.location;
                      const newCenter = { lat: lat(), lng: lng() };
                      setMapCenter(newCenter);
                      map.setCenter(newCenter);
                      createAdvancedMarker(map, newCenter);
                    }
                  });
                }
              }}
            >
            </GoogleMap>
          ) : (
            <Box 
              sx={{ 
                height: '80vh', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center'
              }}
            >
              <Typography variant="h6" color="text.secondary">
                Google Maps API key not configured
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};